/* eslint-disable no-unused-vars */
/* eslint-disable max-len */
import React, { useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";

import { getSignatories } from "../../../../store/selectors/appConfig";

import {
  CustomSelect,
  Input,
  InputGroup,
  AutoSaveField as Field,
  SelectAutocomplete,
  LinkedField,
  InlineRadioGroup
} from "../../../../components/Form";

import { UAE_CODE, UAE } from "../../../../constants";
import { MapPreview } from "../../../../components/Modals/GoogleMap";

import {
  PLACE_OF_BIRTH_LABEL,
  COUNTRY_OF_BIRTH_LABEL,
  YES_NO_OPTIONS,
  MAX_MOTHERS_MAIDEN_NAME_LENGTH,
  MAX_LENGTH_MARITAL_OTHERS_STATUS,
  OTHER_OPTION_CODE
} from "../../constants";

import { getIsEditableStatusSearchInfo } from "../../../../store/selectors/searchProspect";

import {
  PreviewContainer,
  RadioButtonRoot,
  Divider,
  Title,
  ShareholdingField,
  UserInfo,
  Header,
  Placeholder,
  ShareholderName
} from "./styled";
import { Card } from "../../../../components/v2/Card/Card";
import { StakeholdersNamesContext } from "../StakeholdersNameProvider/StakeholdersNameProvider";

/**
 * Determines if fields are optional based on various conditions
 * @param {boolean} isComeFromROScreens - Whether coming from RO screens
 * @param {boolean} isDedupeCase - Whether it's a dedupe case
 * @param {boolean} isExistingCustomerNochange - Whether customer exists with no change
 * @param {boolean} isShareholderACompany - Whether shareholder is a company
 * @param {boolean} isFieldsRequired - Whether fields are required
 * @returns {boolean} Whether fields should be optional
 */

/**
 * Creates a required field label
 * @param {string} baseLabel - The base label text
 * @returns {string} - The formatted label
 */
const createRequiredFieldLabel = baseLabel => baseLabel;

/**
 * Creates an optional field label
 * @param {string} baseLabel - The base label text
 * @returns {string} - The formatted label with optional text
 */
const createOptionalFieldLabel = baseLabel => `${baseLabel} (optional)`;

/**
 * Gets the shareholder name for a company
 * @param {object} signatoryCompanyInfo - Company information
 * @returns {string} - The formatted company name
 */
const getCompanyShareholderName = signatoryCompanyInfo => {
  return `${signatoryCompanyInfo.companyName}`;
};

/**
 * Gets the shareholder name for an individual
 * @param {string} firstName - First name of individual
 * @param {string} middleName - Middle name of individual
 * @param {string} lastName - Last name of individual
 * @returns {string} - The formatted individual name
 */
const getIndividualShareholderName = (firstName, middleName, lastName) => {
  return `${firstName} ${middleName} ${lastName}`;
};

/**
 * Gets the appropriate shareholder name based on type
 * @param {boolean} isCompany - Whether shareholder is a company
 * @param {object} data - The shareholder data
 * @returns {string} - The formatted name
 */
const getShareholderName = (isCompany, data) => {
  const { signatoryCompanyInfo, firstName, middleName, lastName } = data;
  return isCompany
    ? getCompanyShareholderName(signatoryCompanyInfo)
    : getIndividualShareholderName(firstName, middleName, lastName);
};

/**
 * Renders FATCA-related fields based on conditions
 * @param {number} index - Index of the signatory
 * @param {boolean} isShareholderACompany - Whether shareholder is a company
 * @param {boolean} isFieldsOptional - Whether fields are optional
 * @param {object} values - Form values
 * @returns {JSX.Element} - The FATCA fields JSX
 */
export const renderFatcaFields = (index, isShareholderACompany, isFieldsOptional, values) => {
  const hasUSConnection =
    values.hasConnectionWithUS === "Yes" || values.hasConnectionWithUS === true;
  const isEntityOrganizedInUSA =
    values.entityOrganizedInUSA === "Yes" || values.entityOrganizedInUSA === true;

  if (!hasUSConnection) {
    return null;
  }

  return (
    <>
      {isShareholderACompany && (
        <Grid item sm={12} xs={12} mt={4}>
          <Field
            name="entityOrganizedInUSA"
            path={`prospect.signatoryInfo[${index}].fatcaIsEntityUnderUSLaw`}
            label={createRequiredFieldLabel(
              "Is the entity organized in the US or under the laws of the US or any state thereof? "
            )}
            component={InlineRadioGroup}
            options={YES_NO_OPTIONS}
            radioButtonRootComponent={RadioButtonRoot}
            typeRadio
          />
        </Grid>
      )}
      {((isShareholderACompany && isEntityOrganizedInUSA) || !isShareholderACompany) && (
        <Grid item sm={12} xs={12} mt={4}>
          <Field
            name="reportable"
            path={`prospect.signatoryInfo[${index}].reportable`}
            label={createRequiredFieldLabel("Is this reportable?")}
            component={InlineRadioGroup}
            options={YES_NO_OPTIONS}
            radioButtonRootComponent={RadioButtonRoot}
            typeRadio
          />
        </Grid>
      )}
    </>
  );
};

export const PersonalInformationStep = ({
  index,
  id,
  isShareholderACompany,
  isFieldsOptional = false,
  values,
  setFieldValue,
  shareHoldingPercentage,
  stakeholderId,
  stakeholder
}) => {
  const isComeFromROScreens = useSelector(getIsEditableStatusSearchInfo);
  const signatories = useSelector(getSignatories);

  const stakeholdersName = useContext(StakeholdersNamesContext) || [];
  const stakeholderNameData =
    (Array.isArray(stakeholdersName) &&
      stakeholdersName.find(item => {
        if (item?.id) {
          return item.id === stakeholderId;
        }
        return item?.firstName === stakeholder.firstName && item?.lastName === stakeholder.lastName;
      })) ||
    {};

  const { firstName = "", lastName = "", middleName = "" } = stakeholderNameData;
  const signatoryCompanyInfo = stakeholder?.signatoryCompanyInfo;

  const contactDetails = signatories?.[index]?.contactDetails;
  useEffect(() => {
    if (signatories?.[index]?.countryOfBirth === UAE && contactDetails?.primaryHomePhoneNo === "") {
      setFieldValue("homeCountryPrimaryPhoneNo", contactDetails.preferredMobileNo);
      setFieldValue(
        "homeCountryPrimaryPhoneCountryCode",
        contactDetails.primaryHomePhoneNo === contactDetails.preferredMobileNo
          ? contactDetails.preferredMobileCountryCode
          : UAE_CODE
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createChangeProspectHandler = (values, index) => prospect => ({
    ...prospect,
    [`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.country`]: values.countryOfBirth,
    // Ensure emirateCity is cleared when country of birth is not UAE
    [`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.emirateCity`]:
      values.countryOfBirth === UAE
        ? prospect[`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.emirateCity`]
        : ""
  });

  const shareholderName = getShareholderName(isShareholderACompany, {
    signatoryCompanyInfo,
    firstName,
    middleName,
    lastName
  });

  // Label creators for common field patterns
  const poBoxLabel = isFieldsOptional
    ? createOptionalFieldLabel("PO Box Number")
    : createRequiredFieldLabel("PO Box Number");
  const primaryNoLabel = isFieldsOptional
    ? createOptionalFieldLabel("Primary no")
    : createRequiredFieldLabel("Primary no");

  // Extracted label for FATCA "hasConnectionWithUS"
  let hasConnectionWithUSLabel;
  if (isShareholderACompany) {
    hasConnectionWithUSLabel = createRequiredFieldLabel("Is this company a US entity");
  } else {
    hasConnectionWithUSLabel = createRequiredFieldLabel(
      "Does this person have connection with US?"
    );
  }

  return (
    <Card>
      <Header>
        <Placeholder>{shareholderName.charAt(0).toUpperCase()}</Placeholder>
        <UserInfo>
          <ShareholderName sx={{ lineHeight: "0px !important" }}>{shareholderName}</ShareholderName>
          {stakeholder?.isShareholder && (
            <ShareholdingField
              sx={{ lineHeight: "5px !important" }}
            >{`Shareholder ${shareHoldingPercentage}%`}</ShareholdingField>
          )}
        </UserInfo>
      </Header>
      <Divider />

      <Title>Home country details & Personal information</Title>
      <Grid container spacing={3} alignItems={"center"}>
        <Grid item xs={12}>
          <Field
            name="homeCountryBuildingName"
            path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.addressLine1`}
            label="Building name/ no / or floor"
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
      </Grid>
      {!isComeFromROScreens && values.latitude && values.longitude && (
        <div>
          <PreviewContainer>
            <MapPreview
              selectedPosition={{
                lat: Number(values.latitude),
                lng: Number(values.longitude)
              }}
              previewContainerStyle={{
                height: 250,
                borderRadius: 10,
                margin: 10,
                width: "100%",
                marginBottom: 20
              }}
            />
          </PreviewContainer>
        </div>
      )}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Field
          type="hidden"
          name="latitude"
          path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.latitude`}
        />
        <Field
          type="hidden"
          name="longitude"
          path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.longitude`}
        />
        <Grid item sm={6} xs={12}>
          <Field
            name="homeCountryStreetName"
            path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.addressLine2`}
            label="Street number"
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Field
            name="homeCountryArea"
            path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.addressLine3`}
            label="Area or District"
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {values.countryOfBirth === "AE" && (
          <Grid item sm={6} xs={12}>
            <Field
              name="homeCountryEmirate"
              path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.emirateCity`}
              label="Emirate"
              component={SelectAutocomplete}
              datalistId="emirateCity"
            />
          </Grid>
        )}
        <Grid item sm={6} xs={12}>
          <Field
            name="homeCountryZipcode"
            path={`prospect.signatoryInfo[${index}].addressInfo[1].addressDetails.poBox`}
            label={poBoxLabel}
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item sm={6} xs={12}>
          <InputGroup>
            <LinkedField
              name="homeCountryPrimaryPhoneCountryCode"
              linkedPath={`prospect.signatoryInfo[${index}].contactDetails.primaryHomePhoneNo`}
              linkedFieldName="homeCountryPrimaryPhoneNo"
              path={`prospect.signatoryInfo[${index}].contactDetails.primaryHomeCountryCode`}
              component={CustomSelect}
              shrink={false}
              datalistId="countryCode"
              inputProps={{ tabIndex: 0 }}
            />

            <LinkedField
              linkedFieldName="homeCountryPrimaryPhoneCountryCode"
              linkedPath={`prospect.signatoryInfo[${index}].contactDetails.primaryHomeCountryCode`}
              name="homeCountryPrimaryPhoneNo"
              path={`prospect.signatoryInfo[${index}].contactDetails.primaryHomePhoneNo`}
              label={primaryNoLabel}
              placeholder="55xxxxxxx"
              component={Input}
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </InputGroup>
        </Grid>
        <Grid item sm={6} xs={12}>
          <InputGroup>
            <LinkedField
              linkedFieldName="homeCountrySecondaryPhoneNo"
              linkedPath={`prospect.signatoryInfo[${index}].contactDetails.secondaryHomePhoneNo`}
              name="homeCountrySecondaryPhoneCountryCode"
              path={`prospect.signatoryInfo[${index}].contactDetails.secondaryHomeCountryCode`}
              component={CustomSelect}
              shrink={false}
              datalistId="countryCode"
              inputProps={{ tabIndex: 0 }}
            />
            <LinkedField
              name="homeCountrySecondaryPhoneNo"
              linkedFieldName="homeCountrySecondaryPhoneCountryCode"
              linkedPath={`prospect.signatoryInfo[${index}].contactDetails.secondaryHomeCountryCode`}
              path={`prospect.signatoryInfo[${index}].contactDetails.secondaryHomePhoneNo`}
              label="Secondary no (optional)"
              placeholder="55xxxxxx"
              component={Input}
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </InputGroup>
        </Grid>
      </Grid>

      {!isShareholderACompany && renderPersonalDetails(index, isFieldsOptional, values)}

      <Grid spacing={3} container sx={{ mt: 1 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="countryOfBirth"
            path={`prospect.signatoryInfo[${index}].countryOfBirth`}
            datalistId="country"
            label={COUNTRY_OF_BIRTH_LABEL}
            isSearchable
            component={SelectAutocomplete}
            tabIndex="0"
            changeProspect={createChangeProspectHandler(values, index)}
          />
        </Grid>
        {!isShareholderACompany && (
          <Grid item sm={6} xs={12}>
            <Field
              name="placeOfBirth"
              path={`prospect.signatoryInfo[${index}].placeOfBirth`}
              placeholder={PLACE_OF_BIRTH_LABEL}
              label={PLACE_OF_BIRTH_LABEL}
              component={Input}
              InputProps={{
                inputProps: { tabIndex: 0 }
              }}
            />
          </Grid>
        )}
      </Grid>

      <Grid item container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Field
            name="hasConnectionWithUS"
            label={hasConnectionWithUSLabel}
            path={`prospect.signatoryInfo[${index}].signatoryCompanyInfo.hasConnectionWithUS`}
            component={InlineRadioGroup}
            options={YES_NO_OPTIONS}
            radioButtonRootComponent={RadioButtonRoot}
            typeRadio
          />
        </Grid>
        {renderFatcaFields(index, isShareholderACompany, isFieldsOptional, values)}
      </Grid>
    </Card>
  );
};

/**
 * Renders personal details section for individual shareholders
 * @param {number} index - Index of the signatory
 * @param {boolean} isFieldsOptional - Whether fields are optional
 * @param {object} values - Form values
 * @returns {JSX.Element} - The personal details JSX
 */
const renderPersonalDetails = (index, isFieldsOptional, values) => {
  const fathersNameLabel = isFieldsOptional
    ? createOptionalFieldLabel("Father's full name")
    : createRequiredFieldLabel("Father's full name");
  const mothersNameLabel = isFieldsOptional
    ? createOptionalFieldLabel("Mother's full name")
    : createRequiredFieldLabel("Mother's full name");
  const maritalStatusLabel = isFieldsOptional
    ? createOptionalFieldLabel("Marital Status")
    : createRequiredFieldLabel("Marital Status");
  const mothersMaidenNameLabel = isFieldsOptional
    ? createOptionalFieldLabel("Mother's maiden name")
    : createRequiredFieldLabel("Mother's maiden name");

  return (
    <>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="fathersName"
            path={`prospect.signatoryInfo[${index}].fatherFullName`}
            label={fathersNameLabel}
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Field
            name="mothersName"
            path={`prospect.signatoryInfo[${index}].motherFullName`}
            label={mothersNameLabel}
            component={Input}
            InputProps={{
              inputProps: {
                tabIndex: 0
              }
            }}
          />
        </Grid>
      </Grid>

      <Grid spacing={3} container sx={{ mt: 1 }}>
        <Grid item sm={6} xs={12}>
          <Field
            name="maritalStatus"
            path={`prospect.signatoryInfo[${index}].maritalStatus`}
            datalistId="maritalStatus"
            label={maritalStatusLabel}
            isSearchable
            component={SelectAutocomplete}
            tabIndex="0"
          />
        </Grid>
        <Grid item sm={6} xs={12}>
          <Field
            name="mothersMaidenName"
            path={`prospect.signatoryInfo[${index}].mothersMaidenName`}
            label={mothersMaidenNameLabel}
            placeholder="Mother's maiden name"
            component={Input}
            contextualHelpText="Provide mother's surname before marriage"
            InputProps={{
              inputProps: { maxLength: MAX_MOTHERS_MAIDEN_NAME_LENGTH, tabIndex: 0 }
            }}
          />
        </Grid>
        {values.maritalStatus === OTHER_OPTION_CODE && (
          <Grid item md={12} xs={12}>
            <Field
              name="maritalStatusOthers"
              path={`prospect.signatoryInfo[${index}].maritalStatusOthers`}
              label="Other(Specify)"
              placeholder="Other(Specify)"
              component={Input}
              InputProps={{
                inputProps: { maxLength: MAX_LENGTH_MARITAL_OTHERS_STATUS, tabIndex: 0 }
              }}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

/**
 * PersonalInformationStep displays and manages the personal information form for a stakeholder.
 * Handles both company and individual shareholders, field optionality, and map preview.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.index - Index of the signatory in the array
 * @param {string} props.id - Unique identifier for the component
 * @param {boolean} props.isShareholderACompany - Flag indicating if shareholder is a company
 * @param {boolean} [props.isFieldsRequired=true] - Flag indicating if fields are required
 * @param {Object} props.values - Form values object
 * @param {function} props.setFieldValue - Function to set form field values
 * @param {string|number} props.shareHoldingPercentage - Shareholding percentage
 * @param {string} props.stakeholderId - Unique identifier for the stakeholder
 * @param {Object} props.stakeholder - Stakeholder information object
 * @returns {JSX.Element}
 */
PersonalInformationStep.propTypes = {
  /** Index of the signatory in the array */
  index: PropTypes.number.isRequired,
  /** Unique identifier for the component */
  id: PropTypes.string,
  /** Flag indicating if shareholder is a company */
  isShareholderACompany: PropTypes.bool,
  /** Flag indicating if fields are optional */
  isFieldsOptional: PropTypes.bool,
  /** Form values object */
  values: PropTypes.shape({
    /** Latitude coordinate */
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Longitude coordinate */
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Country of birth */
    countryOfBirth: PropTypes.string,
    /** Home country street name */
    homeCountryStreetName: PropTypes.string,
    /** Home country area */
    homeCountryArea: PropTypes.string,
    /** Home country name */
    homeCountryName: PropTypes.string,
    /** Marital status */
    maritalStatus: PropTypes.string,
    /** Flag for connection with US */
    hasConnectionWithUS: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    /** Place of birth */
    placeOfBirth: PropTypes.string,
    /** Mother's maiden name */
    mothersMaidenName: PropTypes.string,
    /** Father's full name */
    fathersName: PropTypes.string,
    /** Mother's full name */
    mothersName: PropTypes.string,
    /** Other marital status (if applicable) */
    maritalStatusOthers: PropTypes.string,
    /** Entity organized in USA (for FATCA fields) */
    entityOrganizedInUSA: PropTypes.oneOfType([PropTypes.string, PropTypes.bool])
  }).isRequired,
  /** Function to set form field values */
  setFieldValue: PropTypes.func.isRequired,
  /** Shareholding percentage */
  shareHoldingPercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Unique identifier for the stakeholder */
  stakeholderId: PropTypes.string,
  /** Stakeholder information object */
  stakeholder: PropTypes.shape({
    /** Signatory company information */
    signatoryCompanyInfo: PropTypes.object,
    /** First name of the stakeholder */
    firstName: PropTypes.string,
    /** Middle name of the stakeholder */
    middleName: PropTypes.string,
    /** Last name of the stakeholder */
    lastName: PropTypes.string,
    isShareholder: PropTypes.bool
  })
};

export const PersonalInformation = PersonalInformationStep;
